import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BriefcaseBusiness, MapPin, SlidersHorizontal, Search } from "lucide-react";
import { Badge } from "components/ui/badge";
import { Input } from "components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Checkbox } from "components/ui/checkbox";
import { Label } from "components/ui/label";
import { ImageWithFallback } from "components/ui/ImageWithFallback";
import { Button } from "components/ui/button";
import { Container } from "react-bootstrap";
import api from "Common/api";
import { toast } from "sonner";
import './recent-contractors.css';

const RELIBUILD_LOGO = "https://tradesmen.s3.amazonaws.com/relibuild-logo/relibuild-logo.png";

interface Contractor {
  id: string;
  name: string;
  specialty: string;
  companyName?: string;
  city?: string;
  image: string;
  location: string;
  skills: string[];
  availability: string;
  relatedTo: {
    type: "project" | "lead";
    name: string;
  };
  dateAdded: string;
}

const contractorExamples: Contractor[] = [
  {
    id: "1",
    name: "David Martinez",
    specialty: "General Contracting",
    image: "https://images.unsplash.com/photo-1764328165995-0624c280a6d2?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBjb25zdHJ1Y3Rpb24lMjB3b3JrZXIlMjBwb3J0cmFpdHxlbnwxfHx8fDE3NzMyNzI2OTl8MA&ixlib=rb-4.1.0&q=80&w=1080",
    location: "Round Rock, TX",
    skills: ["Project Management", "Framing", "Drywall"],
    availability: "Available now",
    relatedTo: {
      type: "project",
      name: "Downtown Office Renovation"
    },
    dateAdded: "2026-03-10"
  },
  {
    id: "2",
    name: "Jennifer Lee",
    specialty: "Interior Painting",
    image: "https://images.unsplash.com/photo-1758876734777-dcc6981f3671?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjBzdXBlcnZpc29yJTIwaGFyZGhhdHxlbnwxfHx8fDE3NzMyMjEwODh8MA&ixlib=rb-4.1.0&q=80&w=1080",
    location: "Austin, TX",
    skills: ["Interior Design", "Color Consultation", "Wallpaper"],
    availability: "Available in 1 week",
    relatedTo: {
      type: "lead",
      name: "Residential Remodel Lead"
    },
    dateAdded: "2026-03-09"
  },
  {
    id: "3",
    name: "Robert Chen",
    specialty: "Electrical Services",
    image: "https://images.unsplash.com/photo-1629010336114-8206b071be71?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxidWlsZGVyJTIwcHJvZmVzc2lvbmFsJTIwcG9ydHJhaXR8ZW58MXx8fHwxNzczMjkyNzk5fDA&ixlib=rb-4.1.0&q=80&w=1080",
    location: "Austin, TX",
    skills: ["Electrical", "Smart Home", "Lighting"],
    availability: "Available now",
    relatedTo: {
      type: "project",
      name: "Smart Home Installation"
    },
    dateAdded: "2026-03-11"
  },
  {
    id: "4",
    name: "Amanda Foster",
    specialty: "Landscaping",
    image: "https://images.unsplash.com/photo-1659353587484-a83a0ddf8aca?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjBlbmdpbmVlciUyMHBvcnRyYWl0fGVufDF8fHx8MTc3MzI5MjgwMHww&ixlib=rb-4.1.0&q=80&w=1080",
    location: "Austin, TX",
    skills: ["Garden Design", "Hardscaping", "Irrigation"],
    availability: "Available in 3 days",
    relatedTo: {
      type: "project",
      name: "Corporate Campus Landscaping"
    },
    dateAdded: "2026-03-12"
  },
  {
    id: "5",
    name: "Michael Johnson",
    specialty: "Plumbing Services",
    image: "https://images.unsplash.com/photo-1746899603348-ab9afd71e16d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxhcmNoaXRlY3QlMjBwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwxfHx8fDE3NzMyOTI4MDB8MA&ixlib=rb-4.1.0&q=80&w=1080",
    location: "San Antonio, TX",
    skills: ["Plumbing", "Pipe Installation", "Water Heaters"],
    availability: "Available now",
    relatedTo: {
      type: "lead",
      name: "Commercial Plumbing Lead"
    },
    dateAdded: "2026-03-08"
  },
  {
    id: "6",
    name: "Sarah Williams",
    specialty: "HVAC Specialist",
    image: "https://images.unsplash.com/photo-1764328165995-0624c280a6d2?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBjb25zdHJ1Y3Rpb24lMjB3b3JrZXIlMjBwb3J0cmFpdHxlbnwxfHx8fDE3NzMyNzI2OTl8MA&ixlib=rb-4.1.0&q=80&w=1080",
    location: "Houston, TX",
    skills: ["HVAC", "Air Conditioning", "Heating"],
    availability: "Available in 2 weeks",
    relatedTo: {
      type: "project",
      name: "Hospital HVAC Upgrade"
    },
    dateAdded: "2026-03-07"
  },
  {
    id: "7",
    name: "Carlos Rodriguez",
    specialty: "Roofing Contractor",
    image: "https://images.unsplash.com/photo-1758876734777-dcc6981f3671?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjBzdXBlcnZpc29yJTIwaGFyZGhhdHxlbnwxfHx8fDE3NzMyMjEwODh8MA&ixlib=rb-4.1.0&q=80&w=1080",
    location: "Dallas, TX",
    skills: ["Roofing", "Shingles", "Repairs"],
    availability: "Available now",
    relatedTo: {
      type: "lead",
      name: "Roof Replacement Lead"
    },
    dateAdded: "2026-03-06"
  },
  {
    id: "8",
    name: "Emily Taylor",
    specialty: "Carpentry",
    image: "https://images.unsplash.com/photo-1629010336114-8206b071be71?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxidWlsZGVyJTIwcHJvZmVzc2lvbmFsJTIwcG9ydHJhaXR8ZW58MXx8fHwxNzczMjkyNzk5fDA&ixlib=rb-4.1.0&q=80&w=1080",
    location: "Austin, TX",
    skills: ["Woodworking", "Custom Furniture", "Cabinets"],
    availability: "Available in 1 week",
    relatedTo: {
      type: "project",
      name: "Custom Home Build"
    },
    dateAdded: "2026-03-05"
  }
];

const exampleSpecialties = [
  "General Contracting",
  "Interior Painting",
  "Electrical Services",
  "Landscaping",
  "Plumbing Services",
  "HVAC Specialist",
  "Roofing Contractor",
  "Carpentry"
];

const exampleLocations = ["Austin, TX", "Round Rock, TX", "Dallas, TX", "Houston, TX", "San Antonio, TX"];

export function RecentContractors() {
  const navigate = useNavigate();
  const [allContractors, setAllContractors] = useState<Contractor[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedSpecialties, setSelectedSpecialties] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [availableOnly, setAvailableOnly] = useState(false);

  useEffect(() => {
    document.title = "Recent Contractors | RELiBUILD";
    setLoading(true);
    api.get("/jobs/recentContractr")
      .then((response: any) => {
        setAllContractors(Array.isArray(response?.data) ? response.data : []);
      })
      .catch((error: any) => {
        setAllContractors([]);
        toast.error(error?.response?.data?.message || "Unable to load recent contractors.");
      })
      .finally(() => setLoading(false));
  }, []);

  const specialties = useMemo(
    () => Array.from(new Set(allContractors.map((contractor) => contractor.specialty).filter(Boolean))).sort(),
    [allContractors]
  );
  const locations = useMemo(
    () => Array.from(new Set(allContractors.map((contractor) => contractor.location).filter(Boolean))).sort(),
    [allContractors]
  );

  // Get unique projects and leads
  const projectsAndLeads = Array.from(
    new Set(allContractors.map((c) => c.relatedTo.name))
  ).sort();

  // Filter and sort contractors
  const filteredContractors = allContractors.filter((contractor) => {
    // Search filter
    const matchesSearch =
      contractor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contractor.specialty.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contractor.skills.some((skill) =>
        skill.toLowerCase().includes(searchQuery.toLowerCase())
      );

    // Specialty filter
    const matchesSpecialty =
      selectedSpecialties.length === 0 ||
      selectedSpecialties.includes(contractor.specialty);

    // Location filter
    const matchesLocation =
      selectedLocations.length === 0 ||
      selectedLocations.includes(contractor.location);

    // Project/Lead filter
    const matchesProject =
      selectedProjects.length === 0 ||
      selectedProjects.includes(contractor.relatedTo.name);

    // Availability filter
    const matchesAvailability =
      !availableOnly || contractor.availability === "Available now";

    return (
      matchesSearch &&
      matchesSpecialty &&
      matchesLocation &&
      matchesProject &&
      matchesAvailability
    );
  });

  // Sort contractors
  const sortedContractors = [...filteredContractors].sort((a, b) => {
    switch (sortBy) {
      case "recent":
        return new Date(b.dateAdded).getTime() - new Date(a.dateAdded).getTime();
      case "name":
        return a.name.localeCompare(b.name);
      default:
        return 0;
    }
  });

  const toggleSpecialty = (specialty: string) => {
    setSelectedSpecialties((prev) =>
      prev.includes(specialty)
        ? prev.filter((s) => s !== specialty)
        : [...prev, specialty]
    );
  };

  const toggleLocation = (location: string) => {
    setSelectedLocations((prev) =>
      prev.includes(location)
        ? prev.filter((l) => l !== location)
        : [...prev, location]
    );
  };

  const toggleProject = (project: string) => {
    setSelectedProjects((prev) =>
      prev.includes(project)
        ? prev.filter((p) => p !== project)
        : [...prev, project]
    );
  };

  const clearFilters = () => {
    setSelectedSpecialties([]);
    setSelectedLocations([]);
    setSelectedProjects([]);
    setAvailableOnly(false);
    setSearchQuery("");
  };

  const activeFiltersCount =
    selectedSpecialties.length +
    selectedLocations.length +
    selectedProjects.length +
    (availableOnly ? 1 : 0);

  return (
    <div className="page-content tailwind-prefix recent-contractors-page">
     <Container fluid>
      <header className="recent-contractors__toolbar bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
    

          {/* Search and Sort */}
          <div className="flex flex-col sm:flex-row gap-3 ">
            <div className="recent-contractors__search app-search-field flex-1 flex">
              <Search className="app-field-icon" />
              <Input
                placeholder="Search contractors by name, specialty, or skills..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 flex flex-1 bg-gray-100"
              />
            </div>

<div className="recent-contractors__sort w-[150px] ">
<Select  value={sortBy} onValueChange={setSortBy} >
              <SelectTrigger className="w-full sm:w-48 ">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">Most Recent</SelectItem>
                <SelectItem value="name">Name (A-Z)</SelectItem>
              </SelectContent>
            </Select>
</div>

<Button variant="outline" onClick={() => setShowFilters(!showFilters)}>
              <SlidersHorizontal className="size-4 mr-2" />
              Filters
              {activeFiltersCount > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                  {activeFiltersCount}
                </span>
              )}
            </Button>
          
          </div>
        </div>
      </header>

      <div className="container mx-auto  py-6">
        <div className="flex gap-6">
          {/* Filters Sidebar */}
          {showFilters && (
            <aside className="recent-contractors__filters w-64 flex-shrink-0">
              <div className="recent-contractors__filters-panel bg-white border border-gray-200 rounded-lg p-4 sticky top-24">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold">Filters</h3>
                  {activeFiltersCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearFilters}
                      className="text-xs"
                    >
                      Clear all
                    </Button>
                  )}
                </div>

                {/* Specialty Filter */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium mb-3">Specialty</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {specialties.map((specialty) => (
                      <div key={specialty} className="flex items-center gap-2">
                        <Checkbox
                          id={`specialty-${specialty}`}
                          checked={selectedSpecialties.includes(specialty)}
                          onCheckedChange={() => toggleSpecialty(specialty)}
                        />
                        <Label
                          htmlFor={`specialty-${specialty}`}
                          className="text-sm cursor-pointer"
                        >
                          {specialty}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Location Filter */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium mb-3">Location</h4>
                  <div className="space-y-2">
                    {locations.map((location) => (
                      <div key={location} className="flex items-center gap-2">
                        <Checkbox
                          id={`location-${location}`}
                          checked={selectedLocations.includes(location)}
                          onCheckedChange={() => toggleLocation(location)}
                        />
                        <Label
                          htmlFor={`location-${location}`}
                          className="text-sm cursor-pointer"
                        >
                          {location}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Project/Lead Filter */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium mb-3">Project/Lead</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {projectsAndLeads.map((project) => (
                      <div key={project} className="flex items-center gap-2">
                        <Checkbox
                          id={`project-${project}`}
                          checked={selectedProjects.includes(project)}
                          onCheckedChange={() => toggleProject(project)}
                        />
                        <Label
                          htmlFor={`project-${project}`}
                          className="text-sm cursor-pointer"
                        >
                          {project}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Availability Filter */}
                <div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="available-now"
                      checked={availableOnly}
                      onCheckedChange={(checked) =>
                        setAvailableOnly(checked as boolean)
                      }
                    />
                    <Label htmlFor="available-now" className="text-sm cursor-pointer">
                      Available Now Only
                    </Label>
                  </div>
                </div>
              </div>
            </aside>
          )}

          {/* Contractors Grid */}
          <div className="recent-contractors__content flex-1">
            {loading ? (
              <div className="recent-contractors__loading flex min-h-[260px] items-center justify-center rounded-lg border border-gray-200 bg-white">
                <div className="text-center">
                  <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-gray-900" />
                  <p className="text-sm text-gray-500">Loading your recent contractor interactions...</p>
                </div>
              </div>
            ) : (
              <>
            <div className="recent-contractors__count">
              <strong>{sortedContractors.length}</strong>
              <span>{sortedContractors.length === 1 ? "contractor" : "contractors"}</span>
              {sortedContractors.length !== allContractors.length ? (
                <small>of {allContractors.length} total</small>
              ) : null}
            </div>

            <div className="recent-contractors__grid grid grid-cols-1 lg:grid-cols-2 gap-6">
              {sortedContractors.map((contractor) => (
                <ContractorCard
                  key={contractor.id}
                  contractor={contractor}
                  onViewProfile={() => navigate(`/recent-contractors/${contractor.id}`)}
                />
              ))}
            </div>

            {sortedContractors.length === 0 && (
              <div className="recent-contractors__empty text-center py-12">
                <p className="text-gray-500 mb-4">
                  {allContractors.length === 0
                    ? "No contractor interactions yet. Contractors connected through a project or job request will appear here."
                    : "No contractors found matching your criteria"}
                </p>
                {allContractors.length > 0 ? <Button variant="outline" onClick={clearFilters}>Clear Filters</Button> : null}
              </div>
            )}
              </>
            )}
          </div>
        </div>
      </div>
      </Container>
    </div>
  );
}

function ContractorCard({
  contractor,
  onViewProfile,
}: {
  contractor: Contractor;
  onViewProfile: () => void;
}) {
  const availabilityValue = String(contractor.availability || "").trim();
  const availability = ["available", "available now", "full time", "full-time"].includes(availabilityValue.toLowerCase())
    ? "Available now"
    : availabilityValue || "Not provided";
  const specialty = String(contractor.specialty || "General contractor")
    .replace(/\belectrian\b/gi, "Electrician");
  const relatedLabel = contractor.relatedTo?.name || "Recent interaction";

  return (
    <article className="recent-contractor-card">
      <div className="recent-contractor-card__header">
        <div className="recent-contractor-card__avatar">
            <ImageWithFallback
              src={contractor.image || RELIBUILD_LOGO}
              fallbackSrc={RELIBUILD_LOGO}
              alt={contractor.name}
              className={contractor.image ? "w-full h-full object-cover" : "w-full h-full object-contain p-2"}
            />
        </div>

        <div className="recent-contractor-card__identity">
          <h3>{contractor.name || "Unnamed contractor"}</h3>
          <p>{specialty}</p>
          <div className="recent-contractor-card__location">
            <MapPin aria-hidden="true" />
            <span>{contractor.location || "Location not provided"}</span>
          </div>
        </div>
      </div>

      <div className="recent-contractor-card__context">
        <span className={`recent-contractor-card__related recent-contractor-card__related--${contractor.relatedTo?.type || "project"}`}>
          <BriefcaseBusiness aria-hidden="true" />
          <span>{relatedLabel}</span>
        </span>
        <span className="recent-contractor-card__availability">
          <i aria-hidden="true" />
          {availability}
        </span>
      </div>

      {contractor.skills?.length ? (
        <div className="recent-contractor-card__skills" aria-label="Skills">
        {contractor.skills.slice(0, 3).map((skill) => (
          <Badge
            key={skill}
            variant="secondary"
            title={skill}
          >
            {skill}
          </Badge>
        ))}
        {contractor.skills.length > 3 ? <small>+{contractor.skills.length - 3} more</small> : null}
        </div>
      ) : null}

      <footer className="recent-contractor-card__footer">
        <button
          type="button"
          onClick={onViewProfile}
          className="recent-contractor-card__action"
        >
          View profile
          <span aria-hidden="true">→</span>
        </button>
      </footer>
    </article>
  );
}
